import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  message, 
  Spin,
  Typography,
  Form,
  Select,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Modal
} from 'antd';
import { 
  GoogleOutlined, 
  FacebookOutlined, 
  BarChartOutlined,
  DatabaseOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Her audit tipi için özel date range'ler
const GOOGLE_ADS_DATE_RANGES = [
  { value: 'TODAY', label: 'Bugün' },
  { value: 'YESTERDAY', label: 'Dün' },
  { value: 'LAST_7_DAYS', label: 'Son 7 Gün' },
  { value: 'LAST_14_DAYS', label: 'Son 14 Gün' },
  { value: 'LAST_30_DAYS', label: 'Son 30 Gün' },
  { value: 'THIS_MONTH', label: 'Bu Ay' },
  { value: 'LAST_MONTH', label: 'Geçen Ay' }
];

const FACEBOOK_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_7d', label: 'Son 7 Gün' },
  { value: 'last_14d', label: 'Son 14 Gün' },
  { value: 'last_30d', label: 'Son 30 Gün' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const GOOGLE_ANALYTICS_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'last_7_days', label: 'Son 7 Gün' },
  { value: 'last_14_days', label: 'Son 14 Gün' },
  { value: 'last_30_days', label: 'Son 30 Gün' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

const CUSTOM_DATA_DATE_RANGES = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'last_week', label: 'Geçen Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'last_month', label: 'Geçen Ay' }
];

// Audit tipleri
const AUDIT_TYPES = [
  { value: 'google_ads', label: 'Google Ads', icon: <GoogleOutlined /> },
  { value: 'facebook', label: 'Facebook Ads', icon: <FacebookOutlined /> },
  { value: 'google_analytics', label: 'Google Analytics', icon: <BarChartOutlined /> },
  { value: 'custom_data', label: 'Özel Veri', icon: <DatabaseOutlined /> }
];

const DemoAudit = () => {
  const [loading, setLoading] = useState(false);
  const [demoAudits, setDemoAudits] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);
  const [selectedAuditType, setSelectedAuditType] = useState(null);
  const [form] = Form.useForm();

  // Audit tipine göre date range'leri getir
  const getDateRangeOptions = () => {
    switch (selectedAuditType) {
      case 'google_ads':
        return GOOGLE_ADS_DATE_RANGES;
      case 'facebook':
        return FACEBOOK_DATE_RANGES;
      case 'google_analytics':
        return GOOGLE_ANALYTICS_DATE_RANGES;
      case 'custom_data':
        return CUSTOM_DATA_DATE_RANGES;
      default:
        return [];
    }
  };

  // Demo auditleri yükle
  const loadDemoAudits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/demo-audits');
      setDemoAudits(response.data);
    } catch (error) {
      console.error('Demo auditler yüklenirken hata:', error);
      message.error('Demo auditler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDemoAudits();
  }, []);

  // Form submit
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const auditData = {
        auditType: values.auditType,
        dateRange: values.dateRange,
        analysis: values.analysis
      };

      if (editingAudit) {
        await api.put(`/demo-audits/${editingAudit.id}`, auditData);
        message.success('Demo audit güncellendi');
      } else {
        await api.post('/demo-audits', auditData);
        message.success('Demo audit oluşturuldu');
      }
      
      setModalVisible(false);
      setEditingAudit(null);
      form.resetFields();
      loadDemoAudits();
    } catch (error) {
      console.error('Demo audit kaydedilirken hata:', error);
      message.error(error.response?.data?.error || 'Demo audit kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  // Demo audit sil
  const handleDelete = async (id) => {
    try {
      await api.delete(`/demo-audits/${id}`);
      message.success('Demo audit silindi');
      loadDemoAudits();
    } catch (error) {
      console.error('Demo audit silinirken hata:', error);
      message.error('Demo audit silinemedi');
    }
  };

  // Audit tipi değişimi handler'ı
  const handleAuditTypeChange = (value) => {
    setSelectedAuditType(value);
    // DateRange'i sıfırla çünkü her audit tipi farklı date range'lere sahip
    form.setFieldsValue({
      auditType: value,
      dateRange: undefined
    });
  };

  // Modal aç
  const openModal = (audit = null) => {
    setEditingAudit(audit);
    setModalVisible(true);
    
    if (audit) {
      setSelectedAuditType(audit.auditType);
      form.setFieldsValue({
        auditType: audit.auditType,
        dateRange: audit.dateRange,
        analysis: audit.analysis
      });
    } else {
      setSelectedAuditType(null);
      form.resetFields();
    }
  };

  // Tablo kolonları
  const columns = [
    {
      title: 'Tip',
      dataIndex: 'auditType',
      key: 'auditType',
      render: (type) => {
        const auditType = AUDIT_TYPES.find(t => t.value === type);
        return (
          <Space>
            {auditType?.icon}
            <span>{auditType?.label}</span>
          </Space>
        );
      }
    },
    {
      title: 'Tarih Aralığı',
      dataIndex: 'dateRange',
      key: 'dateRange',
      render: (range, record) => {
        // Get all possible date ranges to find the label
        const allRanges = [
          ...GOOGLE_ADS_DATE_RANGES,
          ...FACEBOOK_DATE_RANGES,
          ...GOOGLE_ANALYTICS_DATE_RANGES,
          ...CUSTOM_DATA_DATE_RANGES
        ];
        const dateRange = allRanges.find(d => d.value === range);
        return <Tag color="blue">{dateRange?.label || range}</Tag>;
      }
    },
    {
      title: 'Oluşturma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => openModal(record)}
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => {
              Modal.confirm({
                title: 'Demo audit silinsin mi?',
                content: 'Bu işlem geri alınamaz.',
                onOk: () => handleDelete(record.id)
              });
            }}
          />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Demo Audit Yönetimi
              </Title>
              <Text type="secondary">
                Brand ID 1 için demo audit sonuçlarını yönetin
              </Text>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => openModal()}
            >
              Yeni Demo Audit
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={demoAudits}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `Toplam ${total} demo audit`
          }}
        />
      </Card>

      {/* Demo Audit Form Modal */}
      <Modal
        title={editingAudit ? "Demo Audit Düzenle" : "Yeni Demo Audit"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAudit(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{}}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Audit Tipi"
                name="auditType"
                rules={[{ required: true, message: 'Audit tipi seçin' }]}
              >
                <Select placeholder="Audit tipi seçin" onChange={handleAuditTypeChange}>
                  {AUDIT_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Space>
                        {type.icon}
                        {type.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Tarih Aralığı"
                name="dateRange"
                rules={[{ required: true, message: 'Tarih aralığı seçin' }]}
              >
                <Select placeholder="Tarih aralığı seçin">
                  {getDateRangeOptions().map(range => (
                    <Option key={range.value} value={range.value}>
                      {range.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Audit Raporu (Markdown formatında)"
            name="analysis"
            rules={[{ required: true, message: 'Audit raporu girin' }]}
          >
            <TextArea 
              rows={12} 
              placeholder={`# Google Ads Audit Raporu

## Genel Performans

Kampanya performansınız endüstri ortalamasının %23 üzerinde. Son 30 günde toplam 15 kampanya analiz edildi.

### Ana Bulgular
- CTR oranı: %1.41 (hedef: %1.20) ✅
- Ortalama CPC: ₺4.21 (sektör ort: ₺4.50) ✅  
- Conversion rate: %2.58 (hedef: %2.10) ✅

### Öneriler
1. **Bütçe optimizasyonu**: Pazartesi-Çarşamba arası %15 artırın
2. **Negatif kelimeler**: 47 kelime negatif listeye eklenmeli  
3. **Coğrafi hedefleme**: İstanbul/Ankara dışı CPA %34 daha yüksek

### Detaylı Metrikler

| Platform | Harcama | CTR | CPC | ROI |
|----------|---------|-----|-----|-----|
| Google Ads | ₺145,670 | %1.41 | ₺4.21 | 3.2x |
| Display Network | ₺23,450 | %0.89 | ₺2.15 | 1.8x |`}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingAudit ? 'Güncelle' : 'Kaydet'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DemoAudit;